import React, { useState, useEffect, useMemo } from 'react';
import { 
  StyleSheet, 
  View, 
  Text, 
  FlatList, 
  TouchableOpacity, 
  Alert, 
  ActivityIndicator,
  Image
} from 'react-native';
import { NativeStackScreenProps } from '@react-navigation/native-stack';
import { doc, getDoc, collection, query, where, getDocs, addDoc, Timestamp, serverTimestamp, orderBy, limit } from 'firebase/firestore';
import { auth, firestore } from '../../services/firebase';
import { MainTabParamList, MainStackParamList } from '../../navigation/MainNavigator';
import { Routine, Day, DayExercise, Exercise } from '../../types';
import { colors, typography, spacing, borderRadius } from '../../constants/theme';
import { useTheme } from '../../context/ThemeContext';
import { Ionicons } from '@expo/vector-icons';
import Card from '../../components/Card';
import Button from '../../components/Button';
import { MUSCLE_GROUPS } from '../../constants/muscleGroups';

type Props = NativeStackScreenProps<MainTabParamList, 'Workout'>;

// Interfaz para los registros de entrenamientos completados
interface CompletedWorkout {
  id: string;
  dayOrder: number;
  date: Timestamp;
}

// Interfaz para usuarios que comparten la rutina
interface RoutineUser {
  id: string;
  username: string;
  firstName: string;
  lastName: string;
  profilePhoto?: string;
  lastCompletedDay?: number;
  lastCompletedDate?: Timestamp;
}

// Interfaz para agrupar ejercicios
interface GroupedExercise {
  exerciseId: string;
  configurations: {
    id: string; // ID único para cada configuración
    series: number;
    repetitions: number;
    restTime?: number;
  }[];
}

const WorkoutScreen = ({ navigation }: Props) => {
  const [currentRoutine, setCurrentRoutine] = useState<Routine | null>(null);
  const [exercises, setExercises] = useState<Record<string, Exercise>>({});
  const [selectedDayIndex, setSelectedDayIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [completingWorkout, setCompletingWorkout] = useState(false);
  const [completedDays, setCompletedDays] = useState<number[]>([]);
  const [routineUsers, setRoutineUsers] = useState<RoutineUser[]>([]);
  const [showUsers, setShowUsers] = useState(false);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const { theme } = useTheme();

  useEffect(() => {
    fetchCurrentRoutine();
  }, []);

  // Obtener el primer día de la semana (domingo)
  const getStartOfWeek = () => {
    const date = new Date();
    const day = date.getDay(); // 0 = domingo, 1 = lunes, etc.
    
    // Si hoy no es domingo, retrocede hasta el domingo anterior
    date.setDate(date.getDate() - day);
    
    // Establecer la hora a 00:00:00
    date.setHours(0, 0, 0, 0);
    
    return date;
  };

  const fetchCurrentRoutine = async () => {
    setLoading(true);
    try {
      const userId = auth.currentUser?.uid;
      
      if (!userId) {
        throw new Error('Usuario no autenticado');
      }
      
      // Get user data to find current routine ID
      const userRef = doc(firestore, 'users', userId);
      const userDoc = await getDoc(userRef);
      
      if (!userDoc.exists()) {
        throw new Error('No se encontró el perfil de usuario');
      }
      
      const userData = userDoc.data();
      const currentRoutineId = userData.currentRoutineId;
      
      if (!currentRoutineId) {
        // No current routine set
        setCurrentRoutine(null);
        setLoading(false);
        return;
      }
      
      // Get routine data
      const routineRef = doc(firestore, 'routines', currentRoutineId);
      const routineDoc = await getDoc(routineRef);
      
      if (!routineDoc.exists()) {
        throw new Error('No se encontró la rutina');
      }
      
      const routineData = { id: routineDoc.id, ...routineDoc.data() } as Routine;
      setCurrentRoutine(routineData);
      
      // Get all exercise data for the routine
      const exerciseIds = new Set<string>();
      routineData.days.forEach(day => {
        day.exercises.forEach(exercise => {
          exerciseIds.add(exercise.exerciseId);
        });
      });
      
      const exercisesData: Record<string, Exercise> = {};
      
      // Fetch exercises in parallel
      await Promise.all(
        Array.from(exerciseIds).map(async (exerciseId) => {
          const exerciseRef = doc(firestore, 'exercises', exerciseId);
          const exerciseDoc = await getDoc(exerciseRef);
          
          if (exerciseDoc.exists()) {
            exercisesData[exerciseId] = { 
              id: exerciseDoc.id, 
              ...exerciseDoc.data() 
            } as Exercise;
          }
        })
      );
      
      setExercises(exercisesData);
      
      // Una vez que tenemos la rutina, verificamos los días completados esta semana
      await fetchCompletedWorkouts(routineData.id);
      
      // También obtenemos a otros usuarios que usan esta rutina
      fetchRoutineUsers(routineData.id);
    } catch (error) {
      console.error('Error al obtener rutina actual:', error);
      Alert.alert('Error', 'No se pudo cargar la rutina actual');
    } finally {
      setLoading(false);
    }
  };

  // Obtener los usuarios que están utilizando la misma rutina
  const fetchRoutineUsers = async (routineId: string) => {
    try {
      setLoadingUsers(true);
      const userId = auth.currentUser?.uid;
      if (!userId) return;
      
      // Buscar usuarios que tienen esta rutina como actual
      const usersRef = collection(firestore, 'users');
      const usersWithRoutineQuery = query(
        usersRef,
        where('currentRoutineId', '==', routineId)
      );
      
      const querySnapshot = await getDocs(usersWithRoutineQuery);
      const tempUsers: RoutineUser[] = [];
      
      // Para cada usuario, buscar su último entrenamiento completado
      await Promise.all(
        querySnapshot.docs.map(async (userDoc) => {
          // Excluir al usuario actual
          if (userDoc.id === userId) return;
          
          const userData = userDoc.data();
          
          // Buscar el último entrenamiento completado por este usuario para esta rutina
          const historyRef = collection(firestore, 'workoutHistory');
          const historyQuery = query(
            historyRef,
            where('userId', '==', userDoc.id),
            where('routineId', '==', routineId),
            orderBy('date', 'desc'),
            limit(1)
          );
          
          const historySnapshot = await getDocs(historyQuery);
          let lastCompletedDay: number | undefined;
          let lastCompletedDate: Timestamp | undefined;
          
          if (!historySnapshot.empty) {
            const historyData = historySnapshot.docs[0].data();
            lastCompletedDay = historyData.dayOrder;
            lastCompletedDate = historyData.date;
          }
          
          tempUsers.push({
            id: userDoc.id,
            username: userData.username || '',
            firstName: userData.firstName || '',
            lastName: userData.lastName || '',
            profilePhoto: userData.profilePhoto,
            lastCompletedDay,
            lastCompletedDate
          });
        })
      );
      
      setRoutineUsers(tempUsers);
    } catch (error) {
      console.error('Error al obtener usuarios de la rutina:', error);
    } finally {
      setLoadingUsers(false);
    }
  };

  // Obtener los entrenamientos completados esta semana para la rutina actual
  const fetchCompletedWorkouts = async (routineId: string) => {
    try {
      const userId = auth.currentUser?.uid;
      if (!userId) {
        throw new Error('Usuario no autenticado');
      }
      
      const weekStart = getStartOfWeek();
      
      // Consultar los entrenamientos completados esta semana
      const historyRef = collection(firestore, 'workoutHistory');
      const q = query(
        historyRef,
        where('userId', '==', userId),
        where('routineId', '==', routineId),
        where('date', '>=', Timestamp.fromDate(weekStart))
      );
      
      const querySnapshot = await getDocs(q);
      const completedWorkouts: CompletedWorkout[] = [];
      
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        completedWorkouts.push({
          id: doc.id,
          dayOrder: data.dayOrder,
          date: data.date
        });
      });
      
      // Extraer los días completados
      const daysCompleted = completedWorkouts.map(workout => workout.dayOrder);
      setCompletedDays(daysCompleted);
      
      // Determinar el próximo día a mostrar
      if (currentRoutine) {
        const nextDayIndex = determineNextDayIndex(currentRoutine, daysCompleted);
        setSelectedDayIndex(nextDayIndex);
      }
    } catch (error) {
      console.error('Error al obtener entrenamientos completados:', error);
    }
  };

  // Determinar el próximo día de entrenamiento que debe mostrar
  const determineNextDayIndex = (routine: Routine, completedDays: number[]): number => {
    // Si la rutina no tiene días, devolver 0
    if (routine.days.length === 0) {
      return 0;
    }
    
    // Obtener todos los días ordenados por su número
    const orderedDays = [...routine.days].sort((a, b) => a.order - b.order);
    
    // Buscar el primer día que no se ha completado aún
    for (const day of orderedDays) {
      if (!completedDays.includes(day.order)) {
        // Encontrar el índice correspondiente en la rutina
        const dayIndex = routine.days.findIndex(d => d.order === day.order);
        return dayIndex >= 0 ? dayIndex : 0;
      }
    }
    
    // Si todos los días han sido completados, mostrar el primero
    return 0;
  };

  // Agrupar ejercicios similares
  const groupedExercises = useMemo(() => {
    if (!currentRoutine || selectedDayIndex === undefined) return [];

    const selectedDay = currentRoutine.days[selectedDayIndex];
    if (!selectedDay || !selectedDay.exercises) return [];

    const grouped: { [key: string]: GroupedExercise } = {};

    selectedDay.exercises.forEach((exercise, index) => {
      if (!grouped[exercise.exerciseId]) {
        grouped[exercise.exerciseId] = {
          exerciseId: exercise.exerciseId,
          configurations: []
        };
      }

      grouped[exercise.exerciseId].configurations.push({
        id: `${exercise.exerciseId}-${index}`,
        series: exercise.series,
        repetitions: exercise.repetitions,
        restTime: exercise.restTime
      });
    });

    return Object.values(grouped);
  }, [currentRoutine, selectedDayIndex]);

  const handleExercisePress = (exerciseId: string, configIndex: number = 0) => {
    if (!currentRoutine) return;
    
    navigation.getParent()?.navigate('ExerciseDetail', {
      exerciseId: exerciseId,
      routineId: currentRoutine.id,
      dayOrder: selectedDayIndex + 1
    });
  };

  // Función para obtener el nombre del grupo muscular a partir del ID
  const getMuscleGroupName = (muscleGroupId: string): string => {
    const muscleGroup = MUSCLE_GROUPS.find(group => group.id === muscleGroupId);
    return muscleGroup ? muscleGroup.name : muscleGroupId;
  };

  const renderDayTab = ({ item, index }: { item: Day; index: number }) => {
    const isSelected = index === selectedDayIndex;
    const isCompleted = completedDays.includes(item.order);
    
    return (
      <TouchableOpacity
        style={[
          styles.dayTab,
          { 
            backgroundColor: theme.colors.grey.light,
            borderColor: theme.colors.grey.light
          },
          isSelected && { 
            backgroundColor: theme.colors.primary,
            borderColor: theme.colors.primary
          },
          isCompleted && !isSelected && { 
            backgroundColor: theme.colors.grey.light,
            borderColor: theme.colors.success
          }
        ]}
        onPress={() => setSelectedDayIndex(index)}
      >
        <View style={styles.dayTabContent}>
          <Text
            style={[
              styles.dayTabText,
              { color: theme.colors.text },
              isSelected && { color: theme.colors.white, fontWeight: 'bold' },
              isCompleted && !isSelected && { color: theme.colors.success }
            ]}
          >
            Día {item.order}
          </Text>
           
          {isCompleted && (
            <Ionicons 
              name="checkmark-circle" 
              size={14} 
              color={isSelected ? theme.colors.white : theme.colors.success} 
              style={styles.completedIcon}
            />
          )}
        </View>
      </TouchableOpacity>
    );
  };

  const renderExerciseItem = ({ item }: { item: GroupedExercise }) => {
    const exercise = exercises[item.exerciseId];
    
    if (!exercise) {
      return null;
    }
    
    return (
      <Card 
        style={{ marginBottom: spacing.md }}
        onPress={() => handleExercisePress(item.exerciseId)}
      >
        <View style={styles.exerciseHeader}>
          <Text style={[styles.exerciseName, { color: theme.colors.text }]}>{exercise.name}</Text>
          <Ionicons name="chevron-forward" size={20} color={theme.colors.grey.dark} />
        </View>
        
        {item.configurations.map((config, index) => (
          <View key={config.id} style={[
            styles.configurationItem,
            index < item.configurations.length - 1 && styles.configurationDivider
          ]}>
            <Text style={[styles.exerciseDetails, { color: theme.colors.secondary }]}>
              {config.series} series x {config.repetitions} repeticiones
              {config.restTime ? ` • ${config.restTime}s descanso` : ''}
            </Text>
          </View>
        ))}

        <View style={styles.exerciseMuscleGroups}>
          {exercise.muscleGroups.map(groupId => (
            <View key={groupId} style={[styles.muscleGroupTag, { backgroundColor: theme.colors.grey.light }]}>
              <Text style={[styles.muscleGroupTagText, { color: theme.colors.text }]}>
                {getMuscleGroupName(groupId)}
              </Text>
            </View>
          ))}
        </View>
      </Card>
    );
  };

  const renderUserCard = ({ item, index }: { item: RoutineUser; index: number }) => {
    const fullName = `${item.firstName} ${item.lastName}`;
    const totalUsers = routineUsers.length;
    
    // Ancho adaptativo
    const cardStyle = {
      marginBottom: spacing.sm,
      padding: spacing.sm,
      borderColor: theme.colors.grey.light,
      borderWidth: 1,
      flex: 1, // Usar flexbox para adaptarse automáticamente
      margin: spacing.xs,
      maxWidth: totalUsers === 1 ? '100%' : totalUsers === 2 ? '48%' : '31%' as any,
    };
    
    return (
      <Card 
        style={cardStyle as any}
        onPress={() => navigation.getParent()?.navigate('FriendProfile', { friendId: item.id })}
      >
        <View style={styles.userCardContent}>
          <View style={styles.userAvatar}>
            {item.profilePhoto ? (
              <Image source={{ uri: item.profilePhoto }} style={[styles.avatarImage, { backgroundColor: theme.colors.primary }]} />
            ) : (
              <View style={[styles.defaultAvatar, { backgroundColor: theme.colors.primary }]}>
                <Text style={[styles.avatarInitial, { color: theme.colors.white }]}>
                  {(item.firstName && item.firstName.charAt(0)) || 'U'}
                </Text>
              </View>
            )}
          </View>
          
          <View style={styles.userInfo}>
            <Text 
              style={[
                styles.userName,
                { color: theme.colors.text }
              ]}
              numberOfLines={1}
              ellipsizeMode="tail"
            >
              {fullName}
            </Text>
            
            <Text 
              style={[
                styles.userUsername,
                { color: theme.colors.secondary }
              ]}
              numberOfLines={1}
              ellipsizeMode="tail"
            >
              @{item.username}
            </Text>
            
            {item.lastCompletedDay ? (
              <View style={styles.lastDayContainer}>
                <Text style={[styles.lastDay, { color: theme.colors.success }]}>
                  Último: Día {item.lastCompletedDay}
                </Text>
                {item.lastCompletedDate && (
                  <Text style={[styles.lastDate, { color: theme.colors.grey.medium }]}>
                    {new Date(item.lastCompletedDate.toDate()).toLocaleDateString()}
                  </Text>
                )}
              </View>
            ) : (
              <Text style={[styles.noActivity, { color: theme.colors.grey.medium }]}>Sin actividad</Text>
            )}
            
            <View style={styles.viewProfileIndicator}>
              <Ionicons name="chevron-forward-circle" size={14} color={theme.colors.primary} />
              <Text style={[styles.viewProfileText, { color: theme.colors.primary }]}>Ver perfil</Text>
            </View>
          </View>
        </View>
      </Card>
    );
  };

  const completeWorkout = async () => {
    if (!currentRoutine) return;
    
    try {
      setCompletingWorkout(true);
      
      const userId = auth.currentUser?.uid;
      if (!userId) {
        throw new Error('Usuario no autenticado');
      }
      
      const selectedDay = currentRoutine.days[selectedDayIndex];
      
      // Crear registro de entrenamiento completado
      const workoutHistoryData = {
        userId,
        routineId: currentRoutine.id,
        routineName: currentRoutine.name,
        dayOrder: selectedDay.order,
        dayName: `Día ${selectedDay.order}`,
        date: serverTimestamp(),
        duration: 0, // En una versión futura se podría calcular el tiempo real
        exercisesCompleted: selectedDay.exercises.length,
        notes: ''
      };
      
      // Guardar en Firestore
      await addDoc(collection(firestore, 'workoutHistory'), workoutHistoryData);
      
      // Actualizar los días completados
      setCompletedDays([...completedDays, selectedDay.order]);
      
      // Determinar el próximo día de entrenamiento
      const nextDayIndex = determineNextDayIndex(
        currentRoutine, 
        [...completedDays, selectedDay.order]
      );
      setSelectedDayIndex(nextDayIndex);
      
      Alert.alert(
        '¡Entrenamiento Completado!', 
        'Tu progreso ha sido registrado',
        [
          { 
            text: 'Ver Historial', 
            onPress: () => navigation.navigate('Progress')
          },
          { 
            text: 'Aceptar', 
            style: 'default' 
          }
        ]
      );
    } catch (error) {
      console.error('Error al registrar entrenamiento:', error);
      Alert.alert('Error', 'No se pudo registrar el entrenamiento completado');
    } finally {
      setCompletingWorkout(false);
    }
  };

  if (loading) {
    return (
      <View style={[styles.loadingContainer, { backgroundColor: theme.colors.background }]}>
        <ActivityIndicator size="large" color={theme.colors.primary} />
      </View>
    );
  }

  if (!currentRoutine) {
    return (
      <View style={[styles.emptyContainer, { backgroundColor: theme.colors.background }]}>
        <Ionicons name="fitness-outline" size={64} color={theme.colors.grey.medium} />
        <Text style={[styles.emptyTitle, { color: theme.colors.text }]}>No hay rutina activa</Text>
        <Text style={[styles.emptySubtitle, { color: theme.colors.grey.dark }]}>
          Selecciona una rutina para comenzar a entrenar
        </Text>
        <Button
          title="Ver mis Rutinas"
          onPress={() => navigation.navigate('Routines')}
          type="primary"
          style={styles.emptyButton}
        />
      </View>
    );
  }

  const selectedDay = currentRoutine.days[selectedDayIndex];

  return (
    <View style={[styles.container, { backgroundColor: theme.colors.background }]}>
      {/* Routine Info */}
      <View style={[styles.routineInfoContainer, { backgroundColor: theme.colors.primary }]}>
        <Text style={[styles.routineName, { color: theme.colors.white }]}>{currentRoutine.name}</Text>
        <Text style={[styles.routineCreator, { color: theme.colors.white, opacity: 0.8 }]}>
          Creada por: {currentRoutine.creatorId === auth.currentUser?.uid ? 'Tú' : 'Otro usuario'}
        </Text>
      </View>

      {/* Day Tabs */}
      <View style={[styles.daysTabsContainer, { backgroundColor: theme.colors.card }, theme.shadows.light]}>
        <FlatList
          data={currentRoutine.days}
          renderItem={renderDayTab}
          keyExtractor={(item) => `day-${item.order}`}
          horizontal
          showsHorizontalScrollIndicator={false}
          contentContainerStyle={styles.daysTabsList}
        />
      </View>

      {/* Users using this routine */}
      {routineUsers.length > 0 && (
        <View style={[styles.usersSection, { 
          backgroundColor: theme.colors.card,
          borderBottomColor: theme.colors.grey.light 
        }]}>
          <TouchableOpacity
            style={styles.usersSectionHeader}
            onPress={() => setShowUsers(!showUsers)}
          >
            <Text style={[styles.usersSectionTitle, { color: theme.colors.text }]}>
              {routineUsers.length} personas también usan esta rutina
            </Text>
            <Ionicons 
              name={showUsers ? "chevron-up" : "chevron-down"} 
              size={20} 
              color={theme.colors.text} 
            />
          </TouchableOpacity>
          
          {showUsers && (
            <View style={styles.usersContainer}>
              {loadingUsers ? (
                <ActivityIndicator size="small" color={theme.colors.primary} />
              ) : (
                <>
                  <FlatList
                    data={routineUsers}
                    renderItem={renderUserCard}
                    keyExtractor={item => item.id}
                    numColumns={routineUsers.length === 1 ? 1 : routineUsers.length === 2 ? 2 : 3}
                    key={routineUsers.length <= 2 ? routineUsers.length : 3} // Forzar recreación del componente cuando cambia el número de columnas
                    columnWrapperStyle={routineUsers.length > 1 ? styles.usersList : undefined}
                    contentContainerStyle={styles.usersGrid}
                    showsVerticalScrollIndicator={false}
                    scrollEnabled={false}
                  />
                </>
              )}
            </View>
          )}
        </View>
      )}

      {/* Day Exercises */}
      <View style={styles.dayExercisesContainer}>
        <Text style={[styles.dayTitle, { color: theme.colors.text }]}>Día {selectedDay.order}</Text>
        
        {groupedExercises.length > 0 ? (
          <FlatList
            data={groupedExercises}
            renderItem={renderExerciseItem}
            keyExtractor={(item) => `exercise-${item.exerciseId}`}
            contentContainerStyle={styles.exercisesList}
            showsVerticalScrollIndicator={false}
          />
        ) : (
          <View style={[styles.noExercisesContainer, { backgroundColor: theme.colors.background }]}>
            <Text style={[styles.noExercisesText, { color: theme.colors.grey.dark }]}>
              No hay ejercicios para este día
            </Text>
          </View>
        )}
      </View>

      {/* Action Button */}
      <View style={[
        styles.actionButtonContainer, 
        { 
          backgroundColor: theme.colors.card,
          borderTopColor: theme.colors.grey.light 
        }, 
        theme.shadows.medium
      ]}>
        <Button
          title="Completar Entrenamiento"
          onPress={completeWorkout}
          type="primary"
          size="large"
          loading={completingWorkout}
        />
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  emptyContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: spacing.lg,
  },
  emptyTitle: {
    fontSize: typography.fontSize.xl,
    fontWeight: 'bold',
    marginTop: spacing.md,
  },
  emptySubtitle: {
    fontSize: typography.fontSize.md,
    textAlign: 'center',
    marginTop: spacing.sm,
    marginBottom: spacing.lg,
  },
  emptyButton: {
    width: 200,
  },
  routineInfoContainer: {
    padding: spacing.lg,
  },
  routineName: {
    fontSize: typography.fontSize.xl,
    fontWeight: 'bold',
    marginBottom: spacing.xs,
  },
  routineCreator: {
    fontSize: typography.fontSize.sm,
  },
  daysTabsContainer: {
  },
  daysTabsList: {
    padding: spacing.sm,
  },
  dayTab: {
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.sm,
    marginRight: spacing.sm,
    borderRadius: borderRadius.md,
    borderWidth: 1,
  },
  dayTabText: {
    fontSize: typography.fontSize.md,
  },
  dayExercisesContainer: {
    flex: 1,
    padding: spacing.md,
  },
  dayTitle: {
    fontSize: typography.fontSize.lg,
    fontWeight: 'bold',
    marginBottom: spacing.md,
  },
  exercisesList: {
    paddingBottom: spacing.lg,
  },
  exerciseHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: spacing.xs,
  },
  exerciseName: {
    fontSize: typography.fontSize.md,
    fontWeight: 'bold',
  },
  exerciseDetails: {
    fontSize: typography.fontSize.sm,
    marginBottom: spacing.sm,
  },
  exerciseMuscleGroups: {
    flexDirection: 'row',
    flexWrap: 'wrap',
  },
  muscleGroupTag: {
    borderRadius: borderRadius.sm,
    paddingHorizontal: spacing.sm,
    paddingVertical: spacing.xs / 2,
    marginRight: spacing.xs,
    marginBottom: spacing.xs,
  },
  muscleGroupTagText: {
    fontSize: typography.fontSize.xs,
  },
  noExercisesContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  noExercisesText: {
    fontSize: typography.fontSize.md,
  },
  actionButtonContainer: {
    padding: spacing.md,
    borderTopWidth: 1,
  },
  dayTabContent: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  completedIcon: {
    marginLeft: 4,
  },
  // User cards styles
  usersSection: {
    marginBottom: spacing.sm,
    borderBottomWidth: 1,
  },
  usersSectionHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.sm,
  },
  usersSectionTitle: {
    fontSize: typography.fontSize.md,
    fontWeight: '500',
  },
  usersContainer: {
    paddingBottom: spacing.md,
  },
  usersList: {
    justifyContent: 'space-around',
    paddingHorizontal: spacing.md,
  },
  usersGrid: {
    paddingBottom: spacing.sm,
  },
  userCardContent: {
    alignItems: 'center',
  },
  userAvatar: {
    width: 50,
    height: 50,
    borderRadius: 25,
    marginBottom: spacing.xs,
    overflow: 'hidden',
  },
  avatarImage: {
    width: '100%',
    height: '100%',
  },
  defaultAvatar: {
    width: '100%',
    height: '100%',
    justifyContent: 'center',
    alignItems: 'center',
  },
  avatarInitial: {
    fontSize: typography.fontSize.lg,
    fontWeight: 'bold',
  },
  userInfo: {
    width: '100%',
    alignItems: 'center',
  },
  userName: {
    fontSize: typography.fontSize.sm,
    fontWeight: '500',
    textAlign: 'center',
  },
  userUsername: {
    fontSize: typography.fontSize.xs,
    textAlign: 'center',
    marginBottom: spacing.xs,
  },
  lastDayContainer: {
    alignItems: 'center',
  },
  lastDay: {
    fontSize: typography.fontSize.xs,
    fontWeight: '500',
  },
  lastDate: {
    fontSize: typography.fontSize.xs - 2,
  },
  noActivity: {
    fontSize: typography.fontSize.xs,
    fontStyle: 'italic',
  },
  usersHintContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: spacing.sm,
    padding: spacing.xs,
    borderRadius: borderRadius.sm,
    marginHorizontal: spacing.md,
  },
  usersHint: {
    fontSize: typography.fontSize.sm,
    fontWeight: '500',
    marginLeft: spacing.xs,
  },
  viewProfileIndicator: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    marginTop: spacing.xs,
  },
  viewProfileText: {
    fontSize: typography.fontSize.xs,
    marginLeft: spacing.xs,
    fontWeight: '500',
  },
  configurationItem: {
    paddingVertical: spacing.xs / 2,
  },
  configurationDivider: {
    borderBottomWidth: 1,
    borderBottomColor: 'rgba(0,0,0,0.05)',
    marginBottom: spacing.xs / 2,
  },
});

export default WorkoutScreen; 